import { DATA_SOURCE_TYPES } from '@/components/maplibre/consts';
import { LayerBbox, LayerTypeInfo, RasterDataSourceConfig } from '@/components/maplibre/types';
import type { MapLayer } from '@/lib/db-row-types';

const DEFAULT_RASTER_OPACITY = 1;

/**
 * Reads the persisted raster opacity from a map layer's stored style. Raster
 * styles only ever carry a `{ raster: { opacity } }` block (see style-v2), so a
 * defensive read is enough — anything malformed falls back to fully opaque.
 */
function resolveRasterOpacity(mapLayer: MapLayer | undefined): number {
  const raw = mapLayer?.mapbox_style_data;
  if (!raw) {
    return DEFAULT_RASTER_OPACITY;
  }

  const styleData = typeof raw === 'string' ? safeParse(raw) : raw;
  const opacity = (styleData as { raster?: { opacity?: unknown } } | null)?.raster?.opacity;

  return typeof opacity === 'number' ? opacity : DEFAULT_RASTER_OPACITY;
}

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function buildRasterDataSource(
  layerInfo: LayerTypeInfo,
  mapLayers: MapLayer[]
): RasterDataSourceConfig | null {
  if (layerInfo.type !== DATA_SOURCE_TYPES.RASTER) {
    return null;
  }

  const { layer } = layerInfo;
  const mapLayer = mapLayers.find(ml => ml.layer_id === layer.id);

  return {
    id: layer.id,
    name: layer.title || 'Untitled Raster Layer',
    type: DATA_SOURCE_TYPES.RASTER,
    tiles: `${process.env.NEXT_PUBLIC_GEOFORGE_WORKER_URL}/api/tiles/raster/${layer.id}/{z}/{x}/{y}.png`,
    bbox: (layer.bbox ?? null) as LayerBbox | null,
    visible: true,
    minzoom: layer.min_zoom ?? undefined,
    maxzoom: layer.max_zoom ?? undefined,
    style: { raster: { opacity: resolveRasterOpacity(mapLayer) } },
  };
}

export function buildRasterDataSources(
  rasterLayerInfos: LayerTypeInfo[],
  mapLayers: MapLayer[] = []
): RasterDataSourceConfig[] {
  return rasterLayerInfos
    .map(layerInfo => buildRasterDataSource(layerInfo, mapLayers))
    .filter((config): config is RasterDataSourceConfig => config !== null);
}
